import { freeGapAt } from './constants';
import { toDatetimeLocal } from './waveEditPayload';

// The latest of a set of datetime-local values. As text they sort chronologically, so the last one
// wins; absent bounds come through blank and drop out.
const latestLocal = (...values) => values.filter(Boolean).sort().pop() || '';

/**
 * The `min`/`max` a wave's date-time fields carry, so the picker itself greys out the times its
 * match has already given away — pure, so the arithmetic can be tested away from the form.
 *
 * `windowStart` is where the wave being placed OPENS: the Half-Time whistle for a Half-Time →
 * date-time wave, the typed time for a clock window, and null while nothing is placed yet. It picks
 * which free window the fields are held inside (see `freeGapAt`), which is what keeps a hole BEFORE
 * the match's existing waves reachable instead of pinning every new wave after the last one.
 *
 * The end carries two further rules the form validates anyway — it must land after the start it is
 * paired with, and in the future — so that they are visible before submitting rather than after.
 *
 * Returns `{ gap, startMin, startMax, endMin, endMax }` with datetime-local strings, blank wherever
 * nothing bounds that side. Null when there is no window to speak of.
 */
export const scheduleBounds = (gaps, windowStart, now = new Date()) => {
  // A half-typed date-time arrives as an Invalid Date; it places nothing, so it bounds nothing.
  const at = windowStart && !Number.isNaN(windowStart.getTime()) ? windowStart : null;
  const gap = freeGapAt(gaps, at);
  if (!gap) return null;
  const gapStart = gap.start ? toDatetimeLocal(gap.start) : '';
  const gapEnd = gap.end ? toDatetimeLocal(gap.end) : '';
  return {
    gap,
    startMin: gapStart,
    startMax: gapEnd,
    endMin: latestLocal(gapStart, at ? toDatetimeLocal(at) : '', toDatetimeLocal(now)),
    endMax: gapEnd
  };
};

/**
 * Where a wave added to this match goes by default — after everything already on it — as a
 * datetime-local string ready to seed the field with.
 *
 * Blank unless that boundary is EXACT. A predecessor closing at an estimated Full-Time + window has
 * no moment to hand over on, and seeding the guess would present it as one.
 */
export const appendStartLocal = (gaps) => {
  const gap = freeGapAt(gaps, null);
  return gap?.start && !gap.startEstimated ? toDatetimeLocal(gap.start) : '';
};
